import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ClipboardList, Search, ChevronRight, Calendar, Users } from 'lucide-react';
import { BACKEND_URL } from '../lib/config';

export default function AllEvaluations() {
  const { isAdmin } = useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/evaluations`, {
        params: { page, page_size: PAGE_SIZE },
      });
      setEvaluations(res.data.evaluations);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page]); 

  const filtered = evaluations.filter(ev =>
    ev.cycle_name?.toLowerCase().includes(search.toLowerCase()) ||
    ev.title?.toLowerCase().includes(search.toLowerCase()) ||
    ev.batch_name?.toLowerCase().includes(search.toLowerCase()) ||
    ev.location_name?.toLowerCase().includes(search.toLowerCase()) ||
    ev.coach_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const evalPath = (id) => isAdmin ? `/admin/evaluations/${id}` : `/coach/evaluations/${id}`;

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">All Evaluations</h1>
            <p className="text-sm text-muted-foreground">{total} total evaluations</p>
          </div>
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by coach, batch, location, cycle..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-[#111827] border-border/60 h-9"
          />
        </div>

        {loading ? (
          <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-20 bg-[#111827] rounded-xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No evaluations found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(ev => (
              <Link key={ev.id} to={evalPath(ev.id)}>
                <Card className="bg-[#111827] border-border/60 card-hover cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm truncate">{ev.title || ev.cycle_name}</p>
                          <Badge
                            variant="outline"
                            className={`text-[10px] flex-shrink-0 ${ev.status === 'submitted'
                              ? 'border-green-500/30 text-green-400'
                              : 'border-yellow-500/30 text-yellow-400'}`}
                          >
                            {ev.status === 'submitted' ? 'Submitted' : 'In Progress'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users size={11} />{ev.player_count} players
                          </span>
                          <span className="text-xs text-muted-foreground">{ev.batch_name}</span>
                          <span className="text-xs text-muted-foreground">{ev.location_name}</span>
                          {isAdmin && <span className="text-xs text-[#2BB5D8]">{ev.coach_name}</span>}
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar size={11} />{ev.evaluation_date || ev.created_at?.slice(0, 10)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-muted-foreground flex-shrink-0 ml-2" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
